import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {sezioniAccese} from '../../moduli/leggi';
import IconaNova from './IconaNova';
import {pagineDelMenuAccese} from './voci';
import {haLaVoce} from '../../utils/permessi';

/**
 * LA BARRA IN BASSO del telefono (sotto i 1024px; dal computer non c'e'):
 * Home, Altro, il tasto tondo al centro, Giochi, e l'ultimo tasto.
 *   - da ospite: al centro Registrati (viola), in fondo Accedi;
 *   - da dentro: al centro Versa (oro) per chi ha la voce SITE_DEPOSIT, Cassa
 *     per chi puo' solo prelevare, nessun tasto tondo per chi non ha ne'
 *     l'una ne' l'altra (utils/permessi.js); in fondo Conto.
 * Altro, Giochi, Conto, Versa e Registrati aprono il loro pannello dal basso
 * (FogliNova); Accedi la finestra di accesso. I Fornitori stanno dentro
 * Giochi (il tasto largo d'oro). Altro c'e' solo se il modulo Pagine ha
 * almeno una pagina accesa: senza, la barra ha un tasto in meno. Un secondo
 * tocco sullo stesso tasto richiude. Home porta alla Home.
 *
 * QUALE TASTO E' ACCESO: quello aperto (Giochi anche coi Fornitori aperti);
 * se non c'e' niente di aperto, quello della pagina (Home sulla Home, Altro
 * nelle pagine del modulo Pagine, Giochi nelle sezioni dei giochi, Conto
 * nelle pagine del conto, Versa nella Cassa). Il tasto acceso e' una lastra
 * in rilievo con la riga d'oro sotto, come le voci in alto del computer
 * (Nova.css).
 *
 * Testata e barra non si ripetono: sul telefono Accedi e Registrati, Deposita
 * e l'iniziale del conto stanno solo qui (TestataNova).
 *
 * CON LO SPORT ACCESO (skin come demo.sport) la barra e' la stessa, con la
 * SCHEDINA al posto di Altro: Home, Giochi, il tasto tondo, Schedina, e
 * l'ultimo tasto. La Schedina porta il numero delle quote scelte (`quante`)
 * e apre il suo pannello dal basso (GuscioNova). Lo Sport e il Live stanno
 * dentro Giochi, come le altre sezioni, e nella striscia in alto; le pagine
 * di Altro scendono in fondo al pannello Giochi (FogliNova).
 */
export default function BarraNova({dentro, aperto, onTasto, quante = 0}) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const accese = sezioniAccese();
  const giochi = accese.map((s) => s.percorso).concat(['/cerca', '/categoria']);
  const neiGiochi = giochi.some((p) => pathname === p || pathname.startsWith(p + '/'));
  const conSport = accese.some((s) => s.chiave === 'sport');
  /* Con lo sport le pagine di Altro stanno in Giochi: niente tasto Altro. */
  const pagine = conSport ? [] : pagineDelMenuAccese(t);
  const inAltro = !conSport && pagineDelMenuAccese(t).some((p) => pathname === p.percorso);

  /* IL TASTO TONDO da dentro: Versa a chi puo' versare; a chi puo' solo
     prelevare apre la stessa Cassa col suo nome; a chi non ha ne' l'una ne'
     l'altra non c'e', e la barra ha quattro tasti invece di una porta chiusa. */
  const centro = !dentro
    ? {k: 'registrati', icona: 'nuovoUtente', testo: t('Registrati'), classe: ' viola'}
    : haLaVoce('SITE_DEPOSIT') ? {k: 'versa', icona: 'piu', testo: t('Versa'), classe: ''}
      : haLaVoce('SITE_WITHDRAW') ? {k: 'versa', icona: 'portafoglio', testo: t('Cassa'), classe: ''}
        : null;

  let qui = aperto === 'fornitori' ? 'giochi' : aperto;
  if (!qui) {
    if (pathname === '/') qui = 'home';
    else if (inAltro) qui = 'altro';
    else if (conSport && pagineDelMenuAccese(t).some((p) => pathname === p.percorso)) qui = 'giochi';
    else if (dentro && centro && pathname === '/my-account/cassa') qui = 'versa';
    else if (dentro && (pathname.startsWith('/my-account/') || pathname === '/gioco-responsabile')) qui = 'conto';
    else if (neiGiochi) qui = 'giochi';
  }

  const tasto = (k, icona, testo, fai, conta) => (
    <button type={'button'} className={'nova-tab' + (qui === k ? ' on' : '')} aria-pressed={aperto === k}
      onClick={fai || (() => onTasto(k))}>
      <span className={'nova-tab-ic'}>
        <IconaNova nome={icona}/>
        {conta > 0 && <span className={'nova-tab-conta'} aria-label={t('{{count}} selezioni', {count: conta})}>{conta > 99 ? '99+' : conta}</span>}
      </span>
      <span className={'nova-tab-nome'}>{testo}</span>
    </button>
  );
  const tondo = centro && (
    <button type={'button'} className={'nova-tab centro' + centro.classe + (qui === centro.k ? ' on' : '')}
      aria-pressed={aperto === centro.k} onClick={() => onTasto(centro.k)}>
      <span className={'nova-tondo'}><IconaNova nome={centro.icona} misura={26}/></span>
      <span className={'nova-tab-nome'}>{centro.testo}</span>
    </button>
  );
  const giochiTasto = tasto('giochi', 'giochi', t('Giochi'), () => onTasto(aperto === 'fornitori' ? null : 'giochi'));
  const ultimo = dentro ? tasto('conto', 'utente', t('Conto')) : tasto('accedi', 'entra', t('Accedi'));
  const quanti = 2 + (centro ? 1 : 0) + (conSport ? 2 : 1 + (pagine.length ? 1 : 0));
  return (
    <nav className={'nova-barra'} style={{gridTemplateColumns: 'repeat(' + quanti + ', 1fr)'}}
      aria-label={t('Navigazione principale')}>
      {tasto('home', 'home', t('Home'), () => { onTasto(null); navigate('/'); })}
      {conSport ? (
        <>
          {giochiTasto}
          {tondo}
          {tasto('schedina', 'scontrino', t('Schedina'), null, quante)}
        </>
      ) : (
        <>
          {pagine.length > 0 && tasto('altro', 'altro', t('Altro'))}
          {tondo}
          {giochiTasto}
        </>
      )}
      {ultimo}
    </nav>
  );
}
